/**
 * Wire shapes for the endpoints the web client calls.
 *
 * Each interface mirrors the response the owning service actually returns
 * (snake_case, exactly as it appears on the wire) — see `services/tenant`,
 * `services/auth` and the endpoint catalog. Keep them in sync with the service
 * rather than "improving" the names here.
 */
import type { Persona } from './personas';

/** `GET /signup/v1` */
export interface SignupOptions {
  is_signup_enabled: boolean;
  trial_period_days: number;
  consent: { dpa_version: string; ferpa_notice: string; required_acknowledgements: string[] };
  password_policy: { min_length: number; requirements: string[] };
  slug_policy: { min_length: number; max_length: number; pattern: string };
}

/** `GET /signup/v1/slug-availability?slug=` */
export interface SlugAvailability {
  slug: string;
  is_available: boolean;
}

/** `GET /signup/v1/team-lookup?slug=` */
export interface TeamLookup {
  tenant_id: string;
  slug: string;
  name: string;
}

/** One social sign-in button on the fan pages. */
export interface FanSignInProvider {
  slug: string;
  display_name: string;
}

/**
 * `GET /auth/v1/fan/sign-in-options` — public.
 *
 * `providers` is EMPTY until an operator enables one, which is the shipping
 * default: every consumer provider is brokered by Keycloak and stays disabled
 * until its OAuth client exists. The fan pages must therefore work fully with
 * password sign-in alone and simply grow buttons later.
 */
export interface FanSignInOptions {
  realm_tenant_id: string;
  providers: FanSignInProvider[];
  is_mfa_required: boolean;
}

/** `POST /auth/v1/signup/fanatic` request. No team, no payment, no MFA. */
export interface FanaticSignupRequest {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

/** `POST /signup/v1` request. `ferpa_acknowledgement` + `dpa_acceptance` gate it. */
export interface SignupRequest {
  team_name: string;
  tenant_slug: string;
  owner_email: string;
  owner_password: string;
  owner_display_name?: string;
  ferpa_acknowledgement: true;
  dpa_acceptance: true;
}

/** `POST /signup/v1` response (201). */
export interface SignupResult {
  tenant_id: string;
  slug: string;
  name: string;
  owner_user_id: string;
  owner_email: string;
  billing_status: string;
  trial_expires_at: string;
  ferpa_acknowledged_at: string;
  ferpa_acknowledged_by: string;
  subscription: { plan: string; status: string; trial_expires_at: string; seats_limit: number };
  onboarding: {
    cohort_values: number;
    enum_values: number;
    stat_definitions: number;
    success_measure_config_version: number;
  };
}

/** `POST /auth/v1/token` and `/token/refresh`. */
export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
  session_id: string;
  tenant_id: string | null;
  persona: Persona;
  mfa_required: boolean;
  mfa_enrolled: boolean;
}

/** `POST /auth/v1/password/reset/request` — always 202 (anti-enumeration). */
export interface PasswordResetRequested {
  status: 'accepted';
  message: string;
  expires_in_hours: number;
}

/** `PATCH /auth/v1/account` — Fanatic self-service email change. */
export interface AccountEmailChanged {
  user_id: string;
  email: string;
  previous_email: string;
  persona: Persona;
  /** The bearer token's `email` claim stays stale until the next refresh. */
  token_email_stale: boolean;
}

/** One row of `GET /auth/v1/sessions`. */
export interface SessionResource {
  session_id: string;
  user_id: string;
  device: string | null;
  ip_address: string | null;
  user_agent: string | null;
  auth_method: string;
  mfa_verified: boolean;
  created_at: string;
  last_seen: string;
  expires_at: string;
  revoked_at: string | null;
  revocation_reason: string | null;
  status: 'active' | 'expired' | 'revoked';
  /** True for the session making this request. */
  current: boolean;
}

/** `GET /auth/v1/sessions` */
export interface SessionPage {
  data: SessionResource[];
  meta: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

/** `POST /auth/v1/mfa/enroll` */
export interface MfaEnrollment {
  method: 'totp';
  totp_uri: string;
  backup_codes: string[];
  pending_verification: boolean;
}

/** `POST /auth/v1/mfa/verify` */
export interface MfaVerification {
  access_token: string;
  expires_in: number;
  mfa_verified: boolean;
  mfa_enrolled: boolean;
  session_id: string;
}

/**
 * `GET` / `PATCH /auth/v1/account/mfa` — the account's two-factor settings.
 *
 * MFA is opt-in per account: `mfa_enforced` is the flag sign-in gates on
 * (set when enrolment completes, or forced by a Team Admin), `mfa_recommended`
 * is a UI nudge for admin / coaching personas and forces nothing.
 */
export interface MfaSettings {
  user_id: string;
  persona: Persona;
  mfa_enrolled: boolean;
  mfa_enforced: boolean;
  mfa_verified: boolean;
  /** Outstanding for THIS session (`mfa_enforced && !mfa_verified`). */
  mfa_required: boolean;
  mfa_recommended: boolean;
}

/** `PATCH /auth/v1/account/mfa` — the settings plus a re-issued access token. */
export interface MfaSettingsUpdate extends MfaSettings {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  session_id: string;
}

export interface MfaSettingsPatch {
  /** Ask for a code at sign-in (needs an enrolled authenticator). */
  enforced?: boolean;
  /** `false` removes the authenticator and backup codes entirely. */
  enrolled?: false;
  /** Required when weakening security on an account that has a password. */
  current_password?: string;
}

/** `GET /tenants/v1/{tenant_id}` (Team Admin / Platform Admin only). */
export interface TenantSummary {
  tenant_id: string;
  name: string;
  slug: string;
  billing_status: string;
  /**
   * Tenant override for support-request routing (FR-HELP-07). `null` means the
   * platform default (`SUPPORT_RECIPIENT_EMAIL_DEFAULT`) is used.
   */
  support_recipient_email?: string | null;
  /** Who may follow the team as a Fanatic (AC-FANATIC-010). */
  fanatic_access_mode?: FanaticAccessMode;
  plan?: string | null;
  member_count?: number | null;
  seats_limit?: number | null;
  trial_expires_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
}

// ---------------------------------------------------------------------------
// users service (`services/users`) — `/users/v1`
// ---------------------------------------------------------------------------

/** One row of `GET /users/v1` and the body of `GET|PATCH /users/v1/{id}`. */
export interface TeamUser {
  user_id: string;
  email: string;
  display_name: string | null;
  persona: Persona | null;
  mfa_enabled: boolean;
  mfa_enforced: boolean;
  /** False once the membership has been removed (soft delete). */
  active: boolean;
  /** True while an invited account has not set a password. */
  invite_pending: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Seat meter returned alongside the user list; drives the invite form. */
export interface SeatUsage {
  team_name: string | null;
  plan: string | null;
  subscription_status: string | null;
  /** `null` = the plan does not meter staff seats. */
  seats_limit: number | null;
  seats_used: number;
  seats_remaining: number | null;
}

/** `GET /users/v1` */
export interface TeamUserPage {
  items: TeamUser[];
  total: number;
  pagination: { page: number; limit: number; total: number; total_pages: number };
  seats: SeatUsage;
}

/** `GET /users/v1` query parameters. */
export interface TeamUserQuery {
  q?: string;
  persona?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  include_inactive?: boolean;
}

/** `POST /users/v1` request. Omit `password` and keep `send_invite` to invite. */
export interface CreateTeamUserInput {
  email: string;
  persona: Persona;
  display_name?: string;
  password?: string;
  send_invite: boolean;
}

/** `POST /users/v1` response (201). */
export interface CreatedTeamUser extends TeamUser {
  /** Present on the invite path so the admin can copy the link. */
  invite_url: string | null;
  invite_expires_in_days: number | null;
  /** Shown once on the direct-create path, then unrecoverable. */
  temporary_password: string | null;
}

// ---------------------------------------------------------------------------
// roster service (`services/roster`) — `/players/v1` and `/cohort-values/v1`
// ---------------------------------------------------------------------------

/** Catalog categories accepted by `POST /cohort-values/v1` (`COHORT_CATEGORIES`). */
export const COHORT_CATEGORIES = ['grade', 'class', 'squad', 'custom'] as const;
export type CohortCategory = (typeof COHORT_CATEGORIES)[number];

/** One row of `GET /cohort-values/v1`. */
export interface CohortValue {
  id: string;
  tenant_id: string;
  /** Stored on the player row; immutable once created. */
  code: string;
  label: string;
  category: string;
  sort_order: number;
  /** Deprecated codes stay readable on existing players but cannot be assigned. */
  is_deprecated: boolean;
  created_at: string;
  updated_at: string;
}

/** `GET /cohort-values/v1` */
export interface CohortValuePage {
  items: CohortValue[];
  total: number;
  page: number;
  limit: number;
  pagination: { page: number; limit: number; total: number; total_pages: number };
}

/** `GET /cohort-values/v1` query parameters. */
export interface CohortValueQuery {
  category?: string;
  q?: string;
  include_deprecated?: boolean;
  page?: number;
  limit?: number;
}

/** `POST /cohort-values/v1` request (Team Admin). */
export interface CreateCohortValueInput {
  code: string;
  label: string;
  category: CohortCategory;
  /** Omitted → the service appends after the highest order in the category. */
  sort_order?: number;
}

/**
 * One roster row. THIS IS THE COMPLETE SHAPE — there is deliberately no
 * `date_of_birth` and no `age` (AC-ROSTER-003 / AC-ROSTER-004); `cohort` is the
 * platform's only seniority mechanism.
 */
export interface Player {
  player_id: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  /** Catalog code, e.g. `SR`, `10`, `VARSITY`. Always present. */
  cohort: string;
  /** Display text resolved from the catalog; `null` when the code has no row. */
  cohort_label: string | null;
  cohort_category: string | null;
  cohort_is_deprecated: boolean;
  jersey_number: string | null;
  position: string | null;
  school: string | null;
  created_at: string;
  updated_at: string;
}

/** `GET /players/v1` */
export interface PlayerPage {
  items: Player[];
  total: number;
  page: number;
  limit: number;
  pagination: { page: number; limit: number; total: number; total_pages: number };
}

/** `GET /players/v1` query parameters. */
export interface PlayerQuery {
  q?: string;
  cohort?: string;
  position?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

/** `POST /players/v1` request. The service rejects any field not listed here. */
export interface PlayerInput {
  first_name: string;
  last_name: string;
  cohort: string;
  jersey_number?: string | null;
  position?: string | null;
  school?: string | null;
}

/** `PATCH /players/v1/{player_id}` request — every field optional. */
export type PlayerPatch = Partial<PlayerInput>;

// ---------------------------------------------------------------------------
// events service (`services/events`) — `/events/v1` + `…/participation`
// ---------------------------------------------------------------------------

/** `EVENT_TYPES` in `services/events/src/constants.ts`. */
export const EVENT_TYPES = ['game', 'practice', 'scrimmage', 'other'] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** The state machine (FR-EVENTS-03): `pending` → `active` → `closed`. */
export const EVENT_STATUSES = ['pending', 'active', 'closed'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

/**
 * One event row. Named `TeamEvent` rather than `Event` so it never shadows the
 * DOM `Event` type in a component file.
 *
 * The fields marked optional are absent from the redacted `fanatic` projection
 * (`toPublicEventResponse`), so any code that reads them must tolerate
 * `undefined`. `type` is a server-side mirror of `event_type` kept for
 * endpoint-catalog compatibility — prefer `event_type`.
 */
export interface TeamEvent {
  event_id: string;
  season_year: number;
  event_type: EventType;
  type?: string;
  name: string;
  opponent_name: string | null;
  location: string | null;
  scheduled_at: string;
  scheduled_end_at: string | null;
  home_score: number | null;
  away_score: number | null;
  status: EventStatus;
  closed_at: string | null;
  tenant_id?: string;
  closed_reason?: string | null;
  closed_by_actor?: string | null;
  closed_by_role?: string | null;
  /** True when the hourly auto-close job closed it (`closed_reason: auto_timeout`). */
  is_auto_closed?: boolean;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** `GET /events/v1` */
export interface EventPage {
  items: TeamEvent[];
  total: number;
  page: number;
  limit: number;
  pagination: { page: number; limit: number; total: number; total_pages: number };
  /** Echo of the `season` filter, or `null` when unfiltered. */
  season?: number | null;
}

/** `GET /events/v1` query parameters. `type`/`status` accept a comma list. */
export interface EventQuery {
  season?: number;
  type?: string;
  status?: string;
  q?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

/** `POST /events/v1` request. The service rejects any field not listed here. */
export interface EventInput {
  name: string;
  event_type: EventType;
  season_year: number;
  /** ISO-8601 instant. */
  scheduled_at: string;
  scheduled_end_at?: string | null;
  opponent_name?: string | null;
  location?: string | null;
}

/**
 * `PATCH /events/v1/{event_id}` request. `status` may only carry
 * `pending → active`; close and reopen have their own endpoints because both
 * require an audited reason.
 */
export type EventPatch = Partial<EventInput> & {
  status?: EventStatus;
  home_score?: number | null;
  away_score?: number | null;
};

/** `POST /events/v1/{event_id}/close` — reason is REQUIRED (≥10 chars). */
export interface CloseEventInput {
  closed_reason: string;
}

/** `POST /events/v1/{event_id}/reopen` — Team Admin only, reason required. */
export interface ReopenEventInput {
  reason: string;
}

/**
 * One row of the participation checklist. The GET is roster-driven, so every
 * live player appears; `is_recorded: false` means nobody has answered for them
 * yet (the default rendering is "did not participate").
 */
export interface ParticipationEntry {
  player_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  jersey_number: string | null;
  position: string | null;
  cohort: string;
  participated: boolean;
  notes: string | null;
  is_recorded: boolean;
  updated_at: string | null;
  updated_by_user_id: string | null;
}

/** `GET|PUT /events/v1/{event_id}/participation` response envelope. */
export interface ParticipationPage {
  event_id: string;
  participation: ParticipationEntry[];
  total: number;
  participated_count: number;
  unrecorded_count: number;
  /** Server's own verdict on whether this caller may write. */
  can_edit: boolean;
}

/** `PUT /events/v1/{event_id}/participation` request. */
export interface ParticipationUpdateInput {
  participation: { player_id: string; participated: boolean; notes?: string | null }[];
}

// ---------------------------------------------------------------------------
// plays-service — Play Archive (`/plays/v1`)
// ---------------------------------------------------------------------------

/** `none` = single submission, `unresolved` = versions disagree, `resolved` = adjudicated. */
export type ConflictStatus = 'none' | 'unresolved' | 'resolved';

/** The O/D/ST toggle. `all` clears the filter (plays-service `UNIT_FILTERS`). */
export type PlayUnitFilter = 'all' | 'offense' | 'defense' | 'special_teams';

/** Server-backed sort columns (plays-service `PLAY_SORT_FIELDS`). */
export type PlaySortField = 'play_number' | 'recorded_at';

/**
 * `GET /plays/v1` row — mirrors `services/plays/src/http/play-view.ts`.
 *
 * A play is always projected THROUGH its active version: `stats` holds the
 * winning submission's values. Losing versions come from
 * `GET /plays/v1/{play_id}/versions`.
 */
export interface PlayView {
  play_id: string;
  event_id: string;
  tenant_id?: string;
  play_number: number;
  active_version_id: string;
  /** Submissions on record for this play, winning and losing. */
  version_count: number;
  conflict_status: ConflictStatus;
  conflict_detected_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  success_measure: boolean | null;
  success_measure_config_version?: number;
  playbook_ref_unresolved?: boolean;
  recorded_at: string;
  recorded_by: string | null;
  last_edited_at: string | null;
  last_edited_by: string | null;
  active_submitted_by: string | null;
  active_submitted_by_role: string;
  active_submitted_at: string;
  /** Stat values of the active version, snake_case as submitted. */
  stats: Record<string, unknown>;
  warnings?: string[];
  /**
   * Team-wide star (`PATCH /plays/v1/{play_id}/favorite`): every staff member
   * sees the same starred plays. Absent on payloads from before migration 0046.
   */
  is_favorited?: boolean;
  favorited_at?: string | null;
  favorited_by?: string | null;
}

/** `GET /plays/v1` envelope (`toPageResponse`). */
export interface PlayPage {
  items: PlayView[];
  total: number;
  page: number;
  limit: number;
  pagination?: { page: number; limit: number; total: number; total_pages: number };
  /** Filters as the server applied them, for control restore. */
  filters?: Record<string, unknown>;
}

/** One submission, as `GET /plays/v1/{play_id}/versions` returns it. */
export interface PlayVersionView {
  version_id: string;
  play_id: string;
  submitted_by: string | null;
  submitted_by_role: string;
  submitted_at: string;
  client_timestamp: string | null;
  stat_payload: Record<string, unknown>;
  idempotency_key: string | null;
  is_active: boolean;
  /** Only the fields where this version disagrees with the active one. */
  diff_vs_active: Record<string, { active: unknown; version: unknown }>;
}

/** `GET /plays/v1/{play_id}/versions` envelope. */
export interface PlayVersionPage {
  items: PlayVersionView[];
  total: number;
  play_id: string;
  event_id: string;
  play_number: number;
  active_version_id: string;
  conflict_status: ConflictStatus;
  conflict_detected_at: string | null;
  /** Union of every field any losing version disagrees on. */
  differing_fields: string[];
  event_status: string;
  /** The server's own verdict — the UI hides "Resolve" instead of earning a 403. */
  can_resolve: boolean;
}

/**
 * `GET /plays/v1` query string.
 *
 * Column filters (`quarter` … `defensive_blitz`) accept a comma-joined list
 * (`play_type=run,pass`): values within one parameter are OR-ed, different
 * parameters AND-ed. `play_number` takes numbers and inclusive ranges
 * (`10-20,31`). The yes/no fields take a single `true` / `false`.
 */
export interface PlayQuery {
  event_id?: string;
  unit?: PlayUnitFilter;
  conflict_status?: ConflictStatus;
  search?: string;
  quarter?: number | string;
  play_type?: string;
  down?: string;
  hash?: string;
  play_outcome?: string;
  specialty_outcome?: string;
  offensive_formation?: string;
  offensive_personnel?: string;
  defensive_front?: string;
  defensive_coverage?: string;
  opponent_blitz?: 'true' | 'false' | string;
  defensive_blitz?: 'true' | 'false' | string;
  success_measure?: 'true' | 'false' | string;
  /** Starred plays only (`true`) — the Coaches Highlight Starred Plays card. */
  is_favorited?: 'true' | 'false';
  play_number?: string;
  sort?: PlaySortField;
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/**
 * `PATCH /plays/v1/{play_id}` request: the stat fields to change, plus a
 * `reason` that the service REQUIRES (≥10 chars) once the event is closed.
 */
export type PlayPatch = Record<string, unknown> & { reason?: string };

/** `POST /plays/v1/{play_id}/resolve` request. One of the two payloads is required. */
/**
 * `POST /plays/v1/events/{event_id}/insert` body — a missed play slotted in
 * between two logged ones. Same stat fields as `POST /plays/v1` (no
 * `play_number`) plus the 1-based `position` it takes; every live play at or
 * after that position shifts +1 in the same transaction. `reason` (≥10 chars)
 * is required once the event is closed (Team Admin only).
 */
export type InsertPlayInput = Record<string, unknown> & {
  position: number;
  reason?: string;
};

export interface ResolveConflictInput {
  selected_version_id?: string;
  custom_stat_payload?: Record<string, unknown>;
  /** Required (≥10 chars) for a post-close Team Admin override. */
  note?: string;
}

// ---------------------------------------------------------------------------
// stats-service — Coaches Highlight feed
// ---------------------------------------------------------------------------

/** `?unit=` on the highlight feed. Mirror of `HIGHLIGHT_SIDES` in services/stats. */
export type HighlightSide = 'offense' | 'defense';

/**
 * How a key stat's value should be rendered; the server classifies it.
 * `count` is a number of plays ("42 plays"), `yards` a yardage total
 * ("156 yards"); `text` covers play-call terms and the pre-formatted play-call
 * leaders ("Power — 28 yards", "Sweep — 75.0%").
 */
export type KeyStatFormat = 'ratio' | 'percent' | 'text' | 'count' | 'yards';

/**
 * One key stat per side: the ten of FR-COACH-05/06 followed by the seven
 * optional Key Calls metrics (total plays, rushing / passing yards, best and
 * most effective run / pass). Defense keys carry an `_opp` suffix.
 */
export interface HighlightKeyStat {
  key: string;
  name: string;
  /** `null` when the game has no plays that feed this stat yet. */
  value: string | number | null;
  format: KeyStatFormat;
}

export interface HighlightPieSlice {
  label: string;
  count: number;
  percent: number;
}

/** One of the five ranked distributions per side (FR-COACH-04). */
export interface HighlightPieChart {
  key: string;
  name: string;
  total: number;
  /** Plays outside the top-five shortlist, already summed by the server. */
  other_count: number;
  slices: HighlightPieSlice[];
  /** Ranked labels behind the chart — the requirement's "shortlist". */
  shortlist: string[];
}

/** Live game state driving the sticky banner (FR-COACH-02). */
export interface HighlightCurrentDown {
  down: number | null;
  distance: number | null;
  yard_line: number | null;
  first_down_line: number | null;
  possession: 'offense' | 'defense' | 'special_teams' | null;
  quarter: number;
  game_clock: string | null;
}

/** `GET /stats/v1/highlight/{event_id}?unit=offense|defense` */
export interface HighlightFeed {
  eventId: string;
  tenantId: string;
  side: HighlightSide;
  current_down: HighlightCurrentDown;
  key_stats: HighlightKeyStat[];
  pie_charts: HighlightPieChart[];
  pie_charts_by_key: Record<string, HighlightPieChart>;
  play_count: number;
  computed_at: string;
}

// ---------------------------------------------------------------------------
// stats-service — Game Summary, Stat Tracker and the season rollup
// ---------------------------------------------------------------------------

/**
 * One of the three stat units. Mirror of `StatUnitKey` in
 * `services/stats/src/aggregation/stat-catalog.ts`; the wire spelling is
 * lower-snake, not the SCREAMING form used in the live-message catalog.
 */
export type StatUnit = 'offense' | 'defense' | 'special_teams';

/** `?unit=` on `/stats/v1/summary`, `/tracker` and `/season`. */
export type StatUnitFilter = 'all' | StatUnit;

/** Whose snap the play was logged under. */
export type StatPossession = StatUnit;

export type GameResult = 'win' | 'loss' | 'tie';

/**
 * How a derived stat should be rendered. `count` … `list` come from
 * `DERIVED_STAT_CATALOG`; `numeric` … `tag` are the custom-field data types
 * (Task 22 populates those rows).
 */
export type StatValueFormat =
  | 'count'
  | 'yards'
  | 'percent'
  | 'average'
  | 'ratio'
  | 'list'
  | 'numeric'
  | 'boolean'
  | 'enum'
  | 'text'
  | 'tag';

/** One column of the line score (FR-SUMMARY-01). Quarters above 4 are OT. */
export interface QuarterScore {
  quarter: number;
  team_score: number;
  opponent_score: number;
  home_score: number;
  away_score: number;
}

/** `statistics.general` — the score/clock/possession header. */
export interface SummaryGeneral {
  score_by_quarter: QuarterScore[];
  total_score: { home: number; away: number; team: number; opponent: number };
  /** `null` before the first play lands. */
  result: GameResult | null;
  game_clock: string | null;
  quarter: number;
  possession: StatPossession | null;
  is_home: boolean;
  opponent_name: string | null;
  event_status: string | null;
}

/** One possession on the drive-by-drive scoring summary (FR-SUMMARY-02). */
export interface ScoringDrive {
  drive: number;
  possession: 'team' | 'opponent';
  team: 'home' | 'away';
  quarter: number;
  start_yard_line: number | null;
  plays: number;
  yards: number;
  /** `M:SS`, or `null` when the drive crossed a quarter. */
  time_of_possession: string | null;
  result: string;
  points: number;
  scored_by: 'team' | 'opponent' | null;
}

/**
 * A projected stat block: `stat_key` → value. The server projects only the
 * catalog entries flagged `show_in_game_summary`, so the key set is stable but
 * a tenant override can add or drop one — render what arrives.
 */
export type SummaryStatValues = Record<string, unknown>;

export interface GameSummaryStatistics {
  general: SummaryGeneral;
  scoring_summary: ScoringDrive[];
  offense?: SummaryStatValues;
  defense?: SummaryStatValues;
  special_teams?: SummaryStatValues;
}

/** `GET /stats/v1/summary/{event_id}?unit=` */
export interface GameSummaryFeed {
  eventId: string;
  tenantId: string;
  unit: StatUnitFilter;
  play_count: number;
  /** Conflicted plays still awaiting a resolution; the page badges these. */
  unresolved_conflicts: number;
  statistics: GameSummaryStatistics;
  computed_at: string;
}

/** One row of the Stat Tracker sheet. Self-describing: label + format included. */
export interface TrackerStat {
  key: string;
  name: string;
  value: unknown;
  format: StatValueFormat | string;
  /** True for a tenant-defined field from the stat schema (Task 22). */
  is_custom: boolean;
}

export interface TrackerCategory {
  category: string;
  stats: TrackerStat[];
}

export interface TrackerUnitBlock {
  unit: StatUnit;
  categories: TrackerCategory[];
}

/** `GET /stats/v1/tracker/{event_id}?unit=` */
export interface StatTrackerFeed {
  eventId: string;
  tenantId: string;
  unit: StatUnitFilter;
  play_count: number;
  unresolved_conflicts: number;
  units: TrackerUnitBlock[];
  /** Flat view, populated only when a single unit was requested. */
  statistics: TrackerCategory[];
  computed_at: string;
}

export interface SeasonRecord {
  wins: number;
  losses: number;
  ties: number;
}

/** One row of the season schedule table. */
export interface SeasonGameLine {
  event_id: string;
  name: string | null;
  opponent_name: string | null;
  scheduled_at: string | null;
  is_home: boolean;
  result: GameResult | null;
  team_score: number;
  opponent_score: number;
  play_count: number;
}

/** `GET /stats/v1/season?season=YYYY&unit=` — cross-game rollup. */
export interface SeasonRollupFeed {
  season: number | null;
  teamId: string | null;
  tenantId: string | null;
  unit: StatUnitFilter;
  total_events: number;
  record: SeasonRecord;
  points_for: number;
  points_against: number;
  points_for_per_game: number | null;
  points_against_per_game: number | null;
  /** Keyed by unit; only the requested units are present. */
  aggregates: Partial<Record<StatUnit, Record<string, number | string | null>>>;
  games: SeasonGameLine[];
  computed_at: string;
}

/** `GET /stats/v1/season` query string. */
export interface SeasonRollupQuery {
  season: number;
  unit?: StatUnitFilter;
  team_id?: string;
}

// ---------------------------------------------------------------------------
// playbook service (`services/playbook`) — `/playbook/v1` (concept playbook)
// ---------------------------------------------------------------------------

/** `PLAYBOOK_UNITS` in `@gridiron/shared` stat-option-catalog. */
export const PLAYBOOK_UNITS = ['offense', 'defense', 'special_teams'] as const;
export type PlaybookUnit = (typeof PLAYBOOK_UNITS)[number];

/** `enum_values.side`: our team or the expected opponent. */
export const STAT_SIDES = ['own', 'opponent'] as const;
export type StatSide = (typeof STAT_SIDES)[number];

/** `enum_values.category` — every stat the Playbook customises. */
export const STAT_CATEGORIES = [
  'FORMATION',
  'COVERAGE',
  'FRONT',
  'PERSONNEL',
  'MOTION',
  'PROTECTION',
  'OFF_CONCEPT',
  'DEF_CONCEPT',
  'BLITZ',
  'ST_CONCEPT',
  'ST_FORMATION',
  'ST_PERSONNEL',
  'ST_PROTECTION',
  'ST_COVERAGE_SCHEME',
  'ST_RETURN_CONCEPT',
  'ST_RUSH_BLOCK_SCHEME',
  'ST_OPPONENT_ALIGNMENT',
] as const;
export type StatCategory = (typeof STAT_CATEGORIES)[number];

/** One option of one stat on one side (`GET /playbook/v1/stats/{category}/{side}` item). */
export interface StatOption {
  option_id: string;
  category: StatCategory;
  side: StatSide;
  /** Stored code that lands on plays. */
  value: string;
  label: string;
  sort_order: number;
  /** Shipped with the platform (never deleted, only disabled). */
  is_stock: boolean;
  /** Offered on Play Entry. */
  is_enabled: boolean;
  notes: string | null;
  /** Formation glyph inner SVG (formations only). */
  glyph_svg: string | null;
  /**
   * Offensive concepts only: what the concept is designed to be. Picking it
   * on Play Entry derives the play's Designed Type (and prefills Play Type).
   */
  design_type?: 'run' | 'pass' | 'rpo' | null;
  /**
   * Formations only: the `option_id` of the default PERSONNEL option (same
   * side). Picking the formation on Play Entry pre-fills the personnel card.
   */
  personnel_group_id?: string | null;
  /** Times an active play carried this option. */
  usage_count: number;
  created_at: string;
  updated_at: string;
}

/** One stat's definition from `GET /playbook/v1`. */
export interface StatDefinitionView {
  category: StatCategory;
  label: string;
  short_label: string;
  unit: PlaybookUnit;
  sides: StatSide[];
  definition: string;
}

/** One catalog card on the Play Entry board for one phase. */
export interface PlayEntryStatCardView {
  id: string;
  field: string;
  category: StatCategory;
  side: StatSide;
  phase: PlaybookUnit;
  title: string;
  label: string;
}

/** `GET /playbook/v1` */
export interface PlaybookCatalog {
  units: PlaybookUnit[];
  tabs_by_unit: Record<PlaybookUnit, StatCategory[]>;
  stats: StatDefinitionView[];
  play_entry_cards: Record<PlaybookUnit, PlayEntryStatCardView[]>;
  can_write: boolean;
  entitlement: { plan: string | null; is_enabled: boolean };
}

/** `GET /playbook/v1/library` — every enabled option, both sides. */
export interface PlaybookLibrary {
  items: StatOption[];
  play_entry_cards: Record<PlaybookUnit, PlayEntryStatCardView[]>;
}

/** `GET /playbook/v1/stats/{category}/{side}` */
export interface StatOptionList {
  category: StatCategory;
  side: StatSide;
  label: string;
  definition: string;
  items: StatOption[];
  can_write: boolean;
}

/** `POST /playbook/v1/stats/{category}/{side}` */
export interface StatOptionInput {
  label: string;
  /** Stored code; derived from the label when omitted. */
  value?: string;
  notes?: string | null;
  is_enabled?: boolean;
  /** Offensive concepts only (`DESIGN_TYPE_CATEGORIES`); `null` = not set. */
  design_type?: 'run' | 'pass' | 'rpo' | null;
  /** Formations only: default personnel option id (same side); `null` = none. */
  personnel_group_id?: string | null;
}

/** `PATCH /playbook/v1/options/{option_id}` */
export interface StatOptionPatch {
  label?: string;
  notes?: string | null;
  is_enabled?: boolean;
  /** Offensive concepts only; `null` clears it. */
  design_type?: 'run' | 'pass' | 'rpo' | null;
  /** Formations only; `null` clears the default personnel. */
  personnel_group_id?: string | null;
}

/** `DELETE /playbook/v1/options/{option_id}` — 204, or 200 with this when retired instead. */
export interface StatOptionRetired {
  retired: true;
  reason: string;
  option: StatOption | null;
}

// ---------------------------------------------------------------------------
// stat-schema-service — custom stat field registry (FR-STATS-01..04)
// ---------------------------------------------------------------------------

/** `stat_definitions.data_type`. Immutable once a field exists (FR-STATS-04). */
export const STAT_FIELD_DATA_TYPES = ['numeric', 'enum', 'boolean', 'text', 'tag_list'] as const;
export type StatFieldDataType = (typeof STAT_FIELD_DATA_TYPES)[number];

/** `stat_definitions.applies_to`. `general` = every unit (wire alias `all`). */
export const STAT_FIELD_APPLIES_TO = ['offense', 'defense', 'special_teams', 'general'] as const;
export type StatFieldAppliesTo = (typeof STAT_FIELD_APPLIES_TO)[number];

/**
 * Catalog categories an `enum` custom field can draw its options from. A field
 * names a CATEGORY rather than carrying a free list, so renaming "Cover 3" in
 * Team Settings keeps every historical play readable (FR-PLAY-05).
 */
export const STAT_FIELD_ENUM_CATEGORIES = [
  'FORMATION',
  'COVERAGE',
  'FRONT',
  'PERSONNEL',
  'MOTION',
  'PROTECTION',
] as const;
export type StatFieldEnumCategory = (typeof STAT_FIELD_ENUM_CATEGORIES)[number];

export const STAT_FIELD_SORT_FIELDS = ['display_order', 'stat_key', 'label', 'created_at'] as const;
export type StatFieldSortField = (typeof STAT_FIELD_SORT_FIELDS)[number];

/** Mirror of `services/stat-schema/src/constants.ts` STAT_KEY_PATTERN. */
export const STAT_KEY_PATTERN = /^[a-z0-9]+(_[a-z0-9]+)*$/;
export const STAT_KEY_MAX_LENGTH = 64;
export const STAT_LABEL_MAX_LENGTH = 200;

export interface StatFieldEnumOption {
  value: string;
  label: string;
  /**
   * Inner markup of a `viewBox="0 0 120 72"` SVG drawn on the Play Entry
   * button (formations), or null for text-only values. Server-owned art:
   * rendered inside an `<svg>` the client sizes, never as `<img>`.
   */
  glyph_svg?: string | null;
}

/** `board_layouts.scope`: the caller's own row or the team default. */
export type LayoutScope = 'user' | 'team';

/** One row of `GET /users/v1/layouts`. `layout` is the board's BoardLayout JSON. */
export interface BoardLayoutRecord {
  id: string;
  /** Same value as `id` (named-layout endpoints address rows by it). */
  layout_id?: string;
  scope: LayoutScope;
  screen_id: string;
  name: string;
  /**
   * The saved layout's name, unique per (scope, owner, screen). `''` (or
   * absent, older servers) = the legacy unnamed slot.
   */
  layout_name?: string;
  layout: Record<string, unknown>;
  owner_user_id: string | null;
  updated_by: string | null;
  updated_at: string;
}

export interface BoardLayoutPage {
  items: BoardLayoutRecord[];
  /** The caller's selected layout per screen: `{ screen_id: layout_id | null }`. */
  active?: Record<string, string | null>;
}

export interface BoardLayoutInput {
  name: string;
  layout: Record<string, unknown>;
}

/** `POST /users/v1/layouts/{scope}/{screen_id}` — a new NAMED layout. */
export interface NamedLayoutInput {
  layout_name: string;
  layout: Record<string, unknown>;
}

/** `PATCH /users/v1/layouts/id/{layout_id}` — rename and/or save over. */
export interface NamedLayoutPatch {
  layout_name?: string;
  layout?: Record<string, unknown>;
}

/** One row of `GET /stat-schema/v1/enums/{category}` - retired rows included. */
export interface EnumCatalogValue extends StatFieldEnumOption {
  category: string;
  sort_order: number;
  is_builtin: boolean;
  /** Retired: kept for historical plays, not offered on the booth. */
  is_deprecated: boolean;
}

export interface EnumCatalogPage {
  category: string;
  items: EnumCatalogValue[];
  can_write: boolean;
}

/** One row of `GET /stat-schema/v1`. */
export interface StatFieldDefinition {
  id: string;
  /** Alias of `id` emitted by the service. */
  field_id?: string;
  tenant_id: string;
  stat_key: string;
  label: string;
  data_type: StatFieldDataType;
  enum_category: StatFieldEnumCategory | null;
  /** Resolved from `enum_category`, so a form needs no second request. */
  enum_values: StatFieldEnumOption[] | null;
  applies_to: StatFieldAppliesTo;
  /** Platform-defined stat: relabel/disable only, never deletable. */
  is_builtin: boolean;
  /** `false` = retired. Hidden from Play Entry; history stays readable. */
  is_enabled: boolean;
  is_required: boolean;
  hudl_export_key: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

/** Tier headroom (FR-STATS-02). `limit === null` = unlimited (Elite). */
export interface StatFieldCapacity {
  used: number;
  limit: number | null;
  remaining: number | null;
  plan: string | null;
}

/** `GET /stat-schema/v1` */
export interface StatSchemaPage {
  items: StatFieldDefinition[];
  total: number;
  page: number;
  limit: number;
  pagination: { page: number; limit: number; total: number; total_pages: number };
  capacity: StatFieldCapacity;
  enum_categories: { category: string; values: StatFieldEnumOption[] }[];
  can_write: boolean;
}

/** `GET /stat-schema/v1` query parameters. */
export interface StatSchemaQuery {
  /** Filter by unit; `general` fields always come back too. */
  unit?: StatFieldAppliesTo;
  include_builtin?: boolean;
  /** `true` also returns retired fields (admin listing). */
  include_disabled?: boolean;
  q?: string;
  sort_by?: StatFieldSortField;
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/** `POST /stat-schema/v1` — `stat_key` and `data_type` are set once. */
export interface StatFieldInput {
  stat_key: string;
  label: string;
  data_type: StatFieldDataType;
  applies_to: StatFieldAppliesTo;
  enum_category?: StatFieldEnumCategory | null;
  hudl_export_key?: string | null;
  is_required?: boolean;
}

/**
 * `PATCH /stat-schema/v1/{field_id}`. `stat_key` (FR-STATS-03) and `data_type`
 * (FR-STATS-04) are deliberately absent: sending either is a 400.
 */
export interface StatFieldPatch {
  label?: string;
  applies_to?: StatFieldAppliesTo;
  enum_category?: StatFieldEnumCategory;
  hudl_export_key?: string | null;
  is_required?: boolean;
  is_enabled?: boolean;
}

/* ==========================================================================
 * help-service — `/help/v1` (FR-HELP-01..05)
 *
 * Mirrors the wire views in `services/help/src/routes/faqs.ts` and
 * `routes/tooltips.ts`.
 * ======================================================================= */

/** One published FAQ. `persona_tags` empty = visible to all roles. */
export interface FaqEntry {
  id: string;
  question: string;
  answer_markdown: string;
  tags: string[];
  persona_tags: string[];
  sort_order: number;
  /** `platform` = shipped entry, `tenant` = authored by this team. */
  scope: 'platform' | 'tenant';
}

/** `GET /help/v1/faqs?persona=&q=&all_roles=&limit=&offset=` */
export interface FaqQuery {
  persona?: string;
  /** Ignore `persona` and return every published entry. */
  all_roles?: boolean;
  /** Case-insensitive substring over question + answer. */
  q?: string;
  limit?: number;
  offset?: number;
}

export interface FaqPage {
  items: FaqEntry[];
  total: number;
  /** Persona the server filtered by, or `null` when all roles were requested. */
  persona: string | null;
  all_roles: boolean;
  q: string | null;
  limit: number;
  offset: number;
}

/** Which tier of tenant > platform > shipped-default answered for a key. */
export type TooltipSource = 'tenant' | 'platform' | 'default';

/** `GET /help/v1/tooltips?keys=a,b,c` — omit `keys` for the whole registry. */
export interface TooltipResolution {
  tooltips: Record<string, string>;
  sources: Record<string, TooltipSource>;
  /** Requested keys with neither an override nor a shipped default. */
  missing: string[];
  registry_available: boolean;
}

/* --- support-service — `/support/v1` (FR-HELP-06/07/08) ------------------- */

/** Wire enum accepted by `POST /support/v1/requests`. */
export type SupportCategory = 'bug' | 'question' | 'billing' | 'feature_request' | 'other';

export type SupportRequestStatus = 'new' | 'acknowledged' | 'resolved';

/**
 * Client half of the diagnostic bundle (AC-SUPPORT-004).
 *
 * Technical context ONLY. `tenant_id`, `user_id`, `persona` and the timestamp
 * are set server-side from the verified identity, and the server strips the
 * query string from `current_url` — application URLs carry player and event
 * ids. Never add a field here that could carry player, roster or student data.
 */
export interface SupportDiagnosticBundleInput {
  current_url: string;
  app_version: string;
  browser_user_agent: string;
}

/** `POST /support/v1/requests` */
export interface SupportRequestInput {
  category: SupportCategory;
  subject: string;
  description: string;
  diagnostic_bundle?: SupportDiagnosticBundleInput;
  /** Single image ≤ 5 MB. Sent as multipart when present. */
  attachment?: File | null;
}

/** 201 response of `POST /support/v1/requests`. */
export interface SupportRequestCreated {
  id: string;
  tenant_id: string;
  user_id: string;
  category: SupportCategory;
  subject: string;
  status: SupportRequestStatus;
  has_attachment: boolean;
  created_at: string | null;
  notification: { is_enqueued: boolean; job_id: string | null };
}

/**
 * `GET /support/v1/recipient-status` — drives the disabled submit button.
 * `source` reports which FR-HELP-07 tier answered: the team's own override or
 * the platform default.
 */
export interface SupportRecipientStatus {
  is_configured: boolean;
  source: 'tenant' | 'platform' | null;
  /** Present (non-null) only when `is_configured` is false. */
  message: string | null;
  rate_limit: {
    user_hourly_limit: number;
    tenant_hourly_limit: number;
    /** Submissions left this hour, `null` when the caller has no team context. */
    remaining: number | null;
    retry_after_seconds: number;
  };
}

/* ==========================================================================
 * billing-service — `/billing/v1` (FR-BILLING-01..10)
 *
 * Mirrors of the wire views in `services/billing/src/http/billing-view.ts` and
 * `invoice-view.ts`. Provider ids (Stripe customer/price/invoice) are absent by
 * design: the service never puts them on the wire.
 * ======================================================================= */

export type BillingCadence = 'monthly' | 'annual' | 'season';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'cancelled';
export type TenantBillingStatus = 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled';
export type BillingModel = 'card' | 'invoice' | 'manual';
export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';

/** Hard limits a plan (or a tenant price override) can cap. `null` = unlimited. */
export type TierLimitKey =
  'seats' | 'teams' | 'games_per_season' | 'storage_bytes' | 'api_rate_per_min' | 'ws_connections';

export type TierLimits = Record<TierLimitKey, number | null>;
/** Feature bundle: `feature_key → is_included`. Extensible per tier. */
export type PlanFeatures = Record<string, boolean>;

/** One entry of the PUBLIC catalog (`GET /billing/v1/plans`). */
export interface BillingPlan {
  plan_id: string;
  name: string;
  display_name: string;
  price_cents: number;
  price_monthly_cents: number;
  price_annual_cents: number | null;
  billing_cadence: BillingCadence;
  trial_days: number;
  seat_cap: number | null;
  game_cap: number | null;
  storage_cap_bytes: number | null;
  limits: TierLimits;
  features: PlanFeatures;
}

export interface BillingPlanCatalog {
  plans: BillingPlan[];
  count: number;
}

export interface TenantSubscription {
  subscription_id: string;
  tenant_id: string;
  plan_id: string;
  status: SubscriptionStatus;
  billing_cadence: BillingCadence;
  billing_model: BillingModel;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  seats_limit: number | null;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  downgraded_from_plan_id: string | null;
}

/** Effective entitlements — a custom price override is already applied. */
export interface BillingEntitlements {
  plan_id: string;
  plan_name: string;
  plan_display_name: string;
  status: string;
  price_cents: number;
  billing_cadence: BillingCadence;
  limits: TierLimits;
  limit_sources: Record<string, string>;
  features: PlanFeatures;
  feature_keys: string[];
  has_price_override: boolean;
  override_id: string | null;
  overridden_fields: string[];
  is_entitled: boolean;
}

/** Drives the 3-day advance banner and the day-of modal (FR-BILLING-03). */
export interface BillingTrialState {
  is_trialing: boolean;
  trial_ends_at: string | null;
  days_remaining: number | null;
  is_banner_due: boolean;
  is_modal_due: boolean;
  downgrades_to: string | null;
}

export interface BillingUsageRow {
  limit_key: TierLimitKey;
  counter_key: string;
  current: number;
  limit: number | null;
  is_unlimited: boolean;
  is_at_limit: boolean;
}

/** `GET|POST|PATCH /billing/v1/subscriptions[/{tenant_id}]` response. */
export interface BillingSubscriptionView {
  subscription: TenantSubscription | null;
  entitlements: BillingEntitlements;
  trial: BillingTrialState;
  usage: BillingUsageRow[];
  tenant_billing_status: TenantBillingStatus;
}

/** `PATCH /billing/v1/subscriptions/{tenant_id}` — plan change / cancellation. */
export interface SubscriptionPatch {
  plan_id?: string;
  billing_cadence?: BillingCadence;
  cancel_at_period_end?: boolean;
}

/** `POST /billing/v1/checkout-sessions` — the only card-capture door. */
export interface CheckoutSessionInput {
  plan_id: string;
  billing_cadence: BillingCadence;
  coupon_code?: string;
  /** App-relative return targets; absolute URLs are rejected by the service. */
  success_path?: string;
  cancel_path?: string;
}

export interface CheckoutSession {
  session_id: string;
  session_url: string;
  publishable_key: string | null;
  expires_at: string | null;
  card_capture_required: boolean;
  plan_id: string;
  plan_name: string;
  amount_due_cents: number;
  discount_cents: number;
  currency: string;
  coupon_code: string | null;
}

export interface InvoicePdfMetadata {
  url: string | null;
  object_key: string | null;
  byte_size: number | null;
  page_count: number | null;
  checksum: string | null;
  generated_at: string | null;
  source: string | null;
}

export interface Invoice {
  invoice_id: string;
  tenant_id: string;
  subscription_id: string | null;
  invoice_number: string | null;
  amount_cents: number;
  discount_cents: number;
  credit_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  status: InvoiceStatus;
  billing_model: BillingModel;
  description: string | null;
  due_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  paid_method: string | null;
  payment_notes: string | null;
  pdf_url: string | null;
  pdf: InvoicePdfMetadata;
  is_processor_linked: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** Page envelope shared by every paginated billing list. */
export interface BillingPageMeta {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  has_next_page: boolean;
}

/** Payment-failure ladder: drives the "payment required" banner. */
export interface DunningStateView {
  status: string;
  attempt_count: number;
  invoice_id: string | null;
  failed_amount_cents: number | null;
  grace_period_start: string | null;
  grace_period_ends_at: string | null;
  lockdown_at: string | null;
  next_retry_at: string | null;
  last_failure_at: string | null;
  reinstated_at: string | null;
  is_paused: boolean;
  is_locked_down: boolean;
}

export type InvoiceSortField = 'created_at' | 'due_at' | 'amount_cents' | 'status';

export interface InvoicePage {
  items: Invoice[];
  page: BillingPageMeta;
  filters: Record<string, string | null>;
  dunning: DunningStateView;
}

/** `GET /billing/v1/invoices` query parameters. */
export interface InvoiceQuery {
  status?: InvoiceStatus;
  q?: string;
  from?: string;
  to?: string;
  sort?: InvoiceSortField;
  direction?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/** `GET /billing/v1/portal?return_path=…` — provider-hosted billing portal. */
export interface BillingPortalSession {
  url: string;
  expires_at: string | null;
}

// ---------------------------------------------------------------------------
// video service (`services/video`) — `/video/v1`
// ---------------------------------------------------------------------------

/** Tenant-selectable video providers (FR-TENANT-05). */
export const VIDEO_PROVIDER_NAMES = ['hudl', 'youtube', 'disabled'] as const;
export type VideoProviderName = (typeof VIDEO_PROVIDER_NAMES)[number];

/**
 * One entry of `GET /video/v1/providers`.
 *
 * `fanatic_visible` is declared by the server-side adapter and is NOT
 * tenant-configurable — the UI renders it as a badge, never as an input.
 */
export interface VideoProviderCapability {
  provider_name: string;
  label: string;
  fanatic_visible: boolean;
  /** Badge copy: "Visible to fans" / "Coaches only" / "No video embed". */
  description: string;
  is_adapter: boolean;
  requires_embed_config: boolean;
  support_url: string | null;
}

export interface VideoProviderCatalog {
  providers: VideoProviderCapability[];
  default_provider: VideoProviderName;
}

/** `GET|PATCH /video/v1/settings` — Team Admin view of the tenant's selection. */
export interface VideoSettings {
  video_provider: VideoProviderName;
  hudl_embed_url: string | null;
  fanatic_visible: boolean;
  provider_label: string | null;
  provider_description: string | null;
  updated_at: string | null;
  updated_by: string | null;
  providers: VideoProviderCapability[];
}

export interface VideoSettingsPatch {
  video_provider?: VideoProviderName;
  hudl_embed_url?: string | null;
}

/** One per-event embed configuration row (`GET /video/v1/embeds`). */
export interface EventVideoEmbed {
  event_id: string;
  event_name: string | null;
  scheduled_at: string | null;
  provider: 'hudl' | 'youtube';
  embed_url: string | null;
  stream_id: string | null;
  updated_at: string;
}

export type EventVideoEmbedSortField = 'event_name' | 'scheduled_at' | 'updated_at';

export interface EventVideoEmbedQuery {
  q?: string;
  provider?: 'hudl' | 'youtube';
  sort?: EventVideoEmbedSortField;
  direction?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface EventVideoEmbedPage {
  embeds: EventVideoEmbed[];
  meta: { page: number; limit: number; total: number; total_pages: number };
}

export interface EventVideoEmbedInput {
  provider?: 'hudl' | 'youtube';
  embed_url?: string | null;
  stream_id?: string | null;
}

/** `GET /video/v1/embed?event_id=…` — 403 `PROVIDER_NOT_FANATIC_VISIBLE` when gated. */
export interface VideoEmbedInfo {
  event_id: string;
  provider: 'hudl' | 'youtube';
  provider_label: string;
  embed_url: string;
  stream_id: string | null;
  fanatic_visible: boolean;
  support_url: string | null;
}

// ---------------------------------------------------------------------------
// Success Measure configuration (`/plays/v1/success-measure-config`, FR-PLAY-07)
// ---------------------------------------------------------------------------

export const DISTANCE_TO_FIRST_DOWN = 'distance_to_first_down';
export type YardageThreshold = number | typeof DISTANCE_TO_FIRST_DOWN;

export interface SuccessMeasurePhaseRule {
  is_enabled: boolean;
  early_down_yards?: YardageThreshold;
  late_down_yards?: YardageThreshold;
  /**
   * 3rd/4th-down yards when the chains can't be measured (spot not logged).
   * Platform default 10.
   */
  late_down_fallback_yards?: number;
  rule?: string;
  thresholds?: Record<string, YardageThreshold | boolean>;
}

export interface SuccessMeasureConfig {
  version: number;
  offense: { run: SuccessMeasurePhaseRule; pass: SuccessMeasurePhaseRule };
  defense: SuccessMeasurePhaseRule;
  special_teams: Record<string, SuccessMeasurePhaseRule>;
  disabled_play_types?: string[];
}

export interface SuccessMeasureConfigView {
  config: SuccessMeasureConfig;
  platform_defaults: SuccessMeasureConfig;
  /** True while the tenant has never saved an override. */
  is_platform_default: boolean;
  updated_at: string | null;
  can_write: boolean;
}

// ---------------------------------------------------------------------------
// fanatic-service (`services/fanatic`) — `/fanatic/v1` + `/tenants/v1/{id}/fanatic-*`
// ---------------------------------------------------------------------------

/** `tenants.fanatic_access_mode` — who may follow a team. */
export type FanaticAccessMode = 'public-follow' | 'invite-only';

/** Lifecycle of one `fanatic_team_grants` row. */
export type FanaticGrantStatus = 'active' | 'pending_invite' | 'revoked';

/** Server-side envelope shared by every fanatic list endpoint. */
export interface FanaticPagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

/** One row of `GET /fanatic/v1/grants` — the Fanatic's own view of a grant. */
export interface FanaticGrant {
  grant_id: string;
  tenant_id: string;
  team_name: string | null;
  team_slug: string | null;
  fanatic_access_mode: FanaticAccessMode | null;
  status: FanaticGrantStatus;
  granted_at: string | null;
  /** `null` = self-follow; a user id = the Team Admin who issued the invite. */
  granted_by: string | null;
  accepted_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  /** True once a Team Admin revoked: re-following is refused for good. */
  is_revocation_irreversible: boolean;
  invite_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FanaticGrantCounts {
  active: number;
  pending_invite: number;
  revoked: number;
}

/** `GET /fanatic/v1/grants` */
export interface FanaticGrantPage {
  grants: FanaticGrant[];
  counts: FanaticGrantCounts;
  pagination: FanaticPagination;
}

export type FanaticGrantSortField = 'team_name' | 'granted_at' | 'status' | 'updated_at';
export type FanaticGrantStatusFilter = 'all' | FanaticGrantStatus;

export interface FanaticGrantQuery {
  q?: string;
  status?: FanaticGrantStatusFilter;
  sort?: FanaticGrantSortField;
  direction?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

/** What the Follow button on the browse page should do for this row. */
export type FanaticFollowAction =
  'follow' | 'accept_invite' | 'request_invite' | 'blocked' | 'none';

/** One row of `GET /fanatic/v1/teams` (public team directory + own grant state). */
export interface PublicTeam {
  tenant_id: string;
  name: string;
  slug: string;
  fanatic_access_mode: FanaticAccessMode;
  follower_count: number;
  latest_game: string | null;
  latest_game_at: string | null;
  is_self_follow_allowed: boolean;
  is_followed: boolean;
  grant_id: string | null;
  grant_status: FanaticGrantStatus | null;
  follow_action: FanaticFollowAction;
}

/** `GET /fanatic/v1/teams` */
export interface PublicTeamPage {
  teams: PublicTeam[];
  pagination: FanaticPagination;
}

export type PublicTeamSortField = 'name' | 'recent_game' | 'follower_count';

export interface PublicTeamQuery {
  q?: string;
  access_mode?: FanaticAccessMode;
  sort?: PublicTeamSortField;
  direction?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

/** `GET /fanatic/v1/realm` — deployment-level Fanatic IdP metadata. */
export interface FanaticRealmInfo {
  realm: string;
  is_tenant_scoped: boolean;
  app_base_url: string;
  invite_ttl_days: number;
}

/** `GET /fanatic/v1/invites/{token}` */
export interface FanaticInvitePreview {
  tenant_id: string;
  team_name: string | null;
  team_slug: string | null;
  invite_email: string;
  invite_expires_at: string;
  is_expired: boolean;
}

/** `POST /fanatic/v1/invites/{token}/accept` */
export interface FanaticInviteAcceptance {
  grant_id: string;
  tenant_id: string;
  team_name: string | null;
  team_slug: string | null;
  status: FanaticGrantStatus;
  accepted_at: string | null;
}

/** One row of `GET /tenants/v1/{id}/fanatic-grants` — the Team Admin's view. */
export interface TeamFanaticGrant {
  grant_id: string;
  tenant_id: string;
  fanatic_user_id: string | null;
  /** Resolved account email, or the invited address while unclaimed. */
  fanatic_email: string | null;
  fanatic_display_name: string | null;
  status: FanaticGrantStatus;
  granted_at: string | null;
  granted_by: string | null;
  accepted_at: string | null;
  invite_email: string | null;
  invite_expires_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_reason: string | null;
  is_revocation_irreversible: boolean;
  created_at: string;
  updated_at: string;
}

/** `GET /tenants/v1/{id}/fanatic-grants` */
export interface TeamFanaticGrantPage {
  grants: TeamFanaticGrant[];
  total: number;
  /** Live followers regardless of filter. Fanatics never consume a paid seat. */
  active_count: number;
  pagination: FanaticPagination;
}

export type TeamFanaticGrantSortField = 'fanatic_email' | 'granted_at' | 'status' | 'updated_at';

export interface TeamFanaticGrantQuery {
  q?: string;
  status?: FanaticGrantStatusFilter;
  sort?: TeamFanaticGrantSortField;
  direction?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

/**
 * `POST /tenants/v1/{id}/fanatic-invites` (201). There is no notification
 * service yet, so the plaintext link comes back on the response for the admin
 * to copy — the database only ever stores its digest.
 */
export interface SentFanaticInvite extends TeamFanaticGrant {
  invite_token: string;
  invite_link: string;
}

/** `POST /tenants/v1/{id}/fanatic-grants/{grant_id}/revoke` (200). */
export interface RevokedFanaticGrant extends TeamFanaticGrant {
  /** Always `false`: a Team Admin revocation is final (AC-FANATIC-009). */
  is_reversible: boolean;
}

/** One row of `GET /auth/v1/account/identities`. */
export interface LinkedIdentity {
  identity_id: string;
  protocol: string;
  provider: string;
  email: string | null;
  linked_at: string;
  /** False while this is the only remaining way to sign in. */
  can_unlink: boolean;
}

/** `GET /auth/v1/account/identities` */
export interface LinkedIdentityList {
  data: LinkedIdentity[];
  meta: { total: number; min_required: number };
}

// ---------------------------------------------------------------------------
// Hudl import / export (hudl-service, `/hudl/v1`)
// ---------------------------------------------------------------------------

/** Export format chosen up front on the Integrations page. */
export type HudlExportFormat = 'hudl' | 'custom';
export type HudlFileType = 'xlsx' | 'csv';
export type HudlFieldGroup = 'situation' | 'offense' | 'defense' | 'special_teams' | 'custom';

/** One column of a custom export: which Gridiron field, under which header. */
export interface HudlExportColumn {
  field: string;
  header: string;
}

/** `GET /hudl/v1/export/fields` item — one exportable Gridiron field. */
export interface HudlExportField {
  field: string;
  label: string;
  default_header: string;
  group: HudlFieldGroup;
}

/** `GET /hudl/v1/export/fields` */
export interface HudlExportFieldCatalog {
  items: HudlExportField[];
  /** The fixed 19 columns of Hudl's playlist spreadsheet, in order. */
  hudl_columns: { header: string; field: string | null }[];
}

/** A saved custom export layout. */
export interface HudlExportTemplate {
  id: string;
  name: string;
  file_type: HudlFileType;
  columns: HudlExportColumn[];
  updated_at: string;
}

export interface HudlExportTemplatePage {
  items: HudlExportTemplate[];
}

/** `POST /hudl/v1/export/templates` */
export interface HudlExportTemplateInput {
  name: string;
  file_type: HudlFileType;
  columns: HudlExportColumn[];
}

/** `PATCH /hudl/v1/export/templates/{template_id}` */
export type HudlExportTemplatePatch = Partial<HudlExportTemplateInput>;

/** `GET /hudl/v1/export/file` query. */
export interface HudlExportFileQuery {
  event_id: string;
  format: HudlExportFormat;
  template_id?: string;
  columns?: HudlExportColumn[];
  file_type?: HudlFileType;
}

/** A downloaded file plus the name the server suggested. */
export interface DownloadedFile {
  blob: Blob;
  file_name: string | null;
}

/* ------------------------------------------------------------------------
 * Site-wide table export (`/exports/v1`, hudl-service)
 * ---------------------------------------------------------------------- */

/** Pages that export their table (`page_name`). */
export type ExportPage =
  | 'play_archive'
  | 'game_summary'
  | 'stat_tracker'
  | 'playbook'
  | 'roster'
  | 'events'
  | 'team_users'
  | 'fanatics';

/** `custom` = the chosen columns; `hudl` = the fixed Hudl 19 (Play Archive only). */
export type ExportFormat = 'custom' | 'hudl';
export type ExportFileType = 'xlsx' | 'csv';

/** One column of the file: the page's column key and the heading to write. */
export interface ExportColumn {
  field: string;
  header: string;
}

/**
 * `POST /exports/v1/table`. `fields` names the page's columns in the order of
 * each row's cells; `columns` (or `template_id`) picks and orders the file's.
 */
export interface TableExportRequest {
  page_name: ExportPage;
  format?: ExportFormat;
  file_type?: ExportFileType;
  /** File name without extension, e.g. `PlayArchive_Varsity_vs_Wayne_2026-09-25`. */
  file_name?: string;
  sheet_name?: string;
  fields?: readonly string[];
  rows?: readonly (readonly (string | number | boolean | null)[])[];
  columns?: readonly ExportColumn[];
  template_id?: string;
  /** `format: 'hudl'`: the event and the archive's filtered + sorted play ids. */
  event_id?: string;
  play_ids?: readonly string[];
}

/** A saved export layout, shared across the tenant and every exporting page. */
export interface ExportTemplate {
  id: string;
  /** The page it was saved from (`null` = cross-page). */
  page_name: ExportPage | null;
  name: string;
  file_type: ExportFileType;
  columns: ExportColumn[];
  created_at?: string;
  updated_at: string;
}

export interface ExportTemplateInput {
  name: string;
  page_name?: ExportPage | null;
  file_type: ExportFileType;
  columns: readonly ExportColumn[];
}

export type ExportTemplatePatch = Partial<Omit<ExportTemplateInput, 'page_name'>>;

export type HudlColumnStatus = 'mapped' | 'custom' | 'ignored';

export interface HudlPreviewColumn {
  header: string;
  field: string | null;
  status: HudlColumnStatus;
  sample: string[];
}

export interface HudlUnknownValue {
  field: string;
  label: string;
  value: string;
  count: number;
  suggestion: { value: string; label: string } | null;
}

export interface HudlCandidateEvent {
  event_id: string;
  name: string;
  opponent_name: string | null;
  date: string | null;
  play_count: number;
  status: EventStatus;
  score: number;
}

/** `POST /hudl/v1/import/preview` */
export interface HudlImportPreview {
  upload_id: string;
  file_name: string;
  format: HudlExportFormat;
  suggested: { name: string; opponent_name: string | null; date: string | null };
  counts: {
    plays: number;
    offense: number;
    defense: number;
    special_teams: number;
    skipped_clips: number;
  };
  columns: HudlPreviewColumn[];
  unknown_values: HudlUnknownValue[];
  candidate_events: HudlCandidateEvent[];
}

/** Override for how one file column is read (`field: null` = ignore it). */
export interface HudlColumnOverride {
  header: string;
  field: string | null;
}

export type HudlReconcileStatus = 'matched' | 'fills' | 'conflict' | 'hudl_only' | 'ours_only';
export type HudlRowAction = 'update' | 'keep' | 'insert' | 'skip';
export type HudlFieldChoice = 'hudl' | 'ours';

/** Display snapshot of one play (Hudl-style yard line -49..50). */
export interface HudlPlaySnapshot {
  quarter: number | null;
  odk: 'O' | 'D' | 'K';
  down: number | null;
  distance: number | null;
  yard_line: number | null;
  play_type_label: string | null;
  result_label: string | null;
  gain_loss: number | null;
  summary: string;
}

export interface HudlReconcileField {
  field: string;
  label: string;
  hudl: string | number | null;
  ours: string | number | null;
  differs: boolean;
  default: HudlFieldChoice;
}

export interface HudlReconcileRow {
  row_id: string;
  status: HudlReconcileStatus;
  position: number;
  hudl: HudlPlaySnapshot | null;
  ours: (HudlPlaySnapshot & { play_id: string; play_number: number }) | null;
  fields: HudlReconcileField[];
  default_action: HudlRowAction;
}

export type HudlReconcileCounts = Record<HudlReconcileStatus, number>;

/** `POST /hudl/v1/import/{upload_id}/reconcile` */
export interface HudlReconcileResult {
  event_id: string;
  event_name: string;
  rows: HudlReconcileRow[];
  counts: HudlReconcileCounts;
  /** Echo back on commit so the server can tell whether the game changed meanwhile. */
  reconcile_token?: string;
}

export interface HudlReconcileInput {
  event_id: string;
  column_overrides?: HudlColumnOverride[];
}

export interface HudlRowDecision {
  row_id: string;
  action: HudlRowAction;
  fields?: Record<string, HudlFieldChoice>;
}

export type HudlValueAction = 'create' | 'map' | 'ignore';

export interface HudlValueDecision {
  field: string;
  value: string;
  action: HudlValueAction;
  map_to?: string;
}

export type HudlImportTarget =
  | {
      kind: 'new';
      name: string;
      opponent_name: string | null;
      date: string | null;
      event_type: 'game';
    }
  | { kind: 'existing'; event_id: string };

/** `POST /hudl/v1/import/{upload_id}/commit` */
export interface HudlCommitInput {
  target: HudlImportTarget;
  decisions?: HudlRowDecision[];
  value_actions?: HudlValueDecision[];
  column_overrides?: HudlColumnOverride[];
  reconcile_token?: string;
}

export interface HudlCommitResult {
  event_id: string;
  event_name: string;
  created: number;
  updated: number;
  inserted: number;
  unchanged: number;
  skipped: number;
  options_created: number;
  import_id: string;
}

/** `GET /hudl/v1/imports` item. */
export interface HudlImportRecord {
  import_id: string;
  file_name: string;
  event_id: string;
  event_name: string;
  created_at: string;
  created_by_name: string | null;
  counts: { created: number; updated: number; inserted: number; skipped: number };
}

export interface HudlImportRecordPage {
  items: HudlImportRecord[];
}
